import {
  hasImpersonationRecord,
  notifyReadOnlyBlocked,
} from "@/lib/impersonation";

const READ_ONLY_RPCS = new Set([
  "form_status",
  "get_event_registrations",
  "get_hero_stats",
  "get_my_completed_quizruns",
  "get_my_quizrun_answer_key",
  "get_team_member_names",
  "gpx_object_path",
  "gps_track_overview",
  "has_accepted_current_terms",
  "is_admin",
  "list_my_gpx_participations",
  "list_quizrun_track_members",
  "list_trackable_quizruns",
  "list_tracked_participations",
  "my_host_location_ids",
  "participation_result_state",
  "quizrun_passive_think_segments",
  "quizrun_question_count",
  "retrieve_quizrun_submit_info",
  "search_persons_for_team",
  "write_audit_log",
  "write_log",
]);

function requestUrl(input: RequestInfo | URL): URL | null {
  const raw =
    typeof input === "string"
      ? input
      : input instanceof URL
        ? input.toString()
        : input.url;
  try {
    return new URL(raw, window.location.origin);
  } catch {
    return null;
  }
}

function requestMethod(input: RequestInfo | URL, init?: RequestInit): string {
  if (init?.method) return init.method.toUpperCase();
  return input instanceof Request ? input.method.toUpperCase() : "GET";
}

export function isReadOnlyRequestAllowed(
  input: RequestInfo | URL,
  init?: RequestInit,
): boolean {
  if (!hasImpersonationRecord()) return true;

  const url = requestUrl(input);
  if (!url) return false;
  const method = requestMethod(input, init);
  const path = url.pathname;

  if (path.includes("/functions/v1/")) return false;
  if (path.includes("/auth/v1/logout")) {
    return url.searchParams.get("scope") === "local";
  }
  if (path.includes("/auth/v1/user")) {
    return method === "GET" || method === "HEAD";
  }

  if (path.includes("/rest/v1/rpc/")) {
    const rpcName = decodeURIComponent(path.split("/").pop() ?? "");
    return READ_ONLY_RPCS.has(rpcName);
  }

  if (path.includes("/rest/v1/") || path.includes("/storage/v1/")) {
    return method === "GET" || method === "HEAD";
  }

  return true;
}

export async function guardedSupabaseFetch(
  input: RequestInfo | URL,
  init?: RequestInit,
): Promise<Response> {
  if (!isReadOnlyRequestAllowed(input, init)) {
    notifyReadOnlyBlocked();
    return new Response(
      JSON.stringify({ message: "Read-only while viewing as this person." }),
      {
        status: 403,
        headers: { "Content-Type": "application/json" },
      },
    );
  }
  return fetch(input, init);
}
